import type {
  StorageRow,
  OpsRow,
  IndexRow,
  ServerRow,
  CostRecord,
  TierRecord,
  ServiceCostRecord,
  DeltaFile,
  MetricsFile,
  DeepReport,
  Recommendation
} from '../../types/atlas-intel.types'

// ─── CSV parsing ──────────────────────────────────────────────────────────────

function parseCSVLine(line: string): string[] {
  const values: string[] = []
  let current = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (ch === '"') {
      inQuotes = !inQuotes
    } else if (ch === ',' && !inQuotes) {
      values.push(current.trim())
      current = ''
    } else {
      current += ch
    }
  }
  values.push(current.trim())
  return values
}

function parseCSV(text: string): Record<string, string>[] {
  const lines = text
    .trim()
    .split('\n')
    .filter((l) => l.trim())
  if (lines.length < 2) return []
  const headers = parseCSVLine(lines[0])
  return lines.slice(1).map((line) => {
    const values = parseCSVLine(line)
    const obj: Record<string, string> = {}
    headers.forEach((h, i) => {
      obj[h] = values[i] ?? ''
    })
    return obj
  })
}

// ─── Storage CSV ──────────────────────────────────────────────────────────────

export function parseStorageCSV(text: string): StorageRow[] {
  return parseCSV(text).filter((r) => r.cluster && r.collection) as unknown as StorageRow[]
}

// ─── Ops CSV ──────────────────────────────────────────────────────────────────

export function parseOpsCSV(text: string): OpsRow[] {
  return parseCSV(text).filter((r) => r.cluster && r.collection) as unknown as OpsRow[]
}

// ─── Indexes CSV ──────────────────────────────────────────────────────────────

export function parseIndexesCSV(text: string): IndexRow[] {
  return parseCSV(text).filter((r) => r.cluster && r.index_name) as unknown as IndexRow[]
}

// ─── Server CSV ───────────────────────────────────────────────────────────────

export function parseServerCSV(text: string): ServerRow[] {
  return parseCSV(text).filter((r) => r.cluster) as unknown as ServerRow[]
}

// ─── JSON parsers ─────────────────────────────────────────────────────────────

export function parseCostsJSON(text: string): CostRecord[] {
  try {
    const parsed = JSON.parse(text)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter((r) => r && (r.c || r.clusterName))
      .map((r) => ({
        c: r.c ?? r.clusterName ?? '',
        d: r.d ?? r.usageDate ?? '',
        a: Number(r.a ?? r.usageAmount ?? 0),
        s: r.s ?? r.serviceName,
        id: r.id ?? r.clusterId
      }))
  } catch {
    return []
  }
}

export function parseTiersJSON(text: string): TierRecord[] {
  try {
    const parsed = JSON.parse(text)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((r) => r && r.name)
  } catch {
    return []
  }
}

export function parseCostsByServiceJSON(text: string): ServiceCostRecord[] {
  try {
    const parsed = JSON.parse(text)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter((r) => r)
      .map((r) => ({
        s: r.s ?? r.service ?? 'Unknown',
        d: r.d ?? r.usageDate ?? '',
        a: Number(r.a ?? r.usageAmount ?? 0)
      }))
  } catch {
    return []
  }
}

export function parseDeltaJSON(text: string): DeltaFile | null {
  try {
    const parsed = JSON.parse(text)
    if (!parsed || !Array.isArray(parsed.clusters)) return null
    return parsed as DeltaFile
  } catch {
    return null
  }
}

export function parseMetricsJSON(text: string): MetricsFile | null {
  try {
    const parsed = JSON.parse(text)
    if (!parsed || !Array.isArray(parsed.clusters)) return null
    return parsed as MetricsFile
  } catch {
    return null
  }
}

// ── Query correlation enrichment ──────────────────────────────────────────────
// Mirrors the ESR logic in atlas-intel-server.mjs so enrichment works even
// when data comes from uploaded files (not the live stub server).

const RANGE_FIELD_RE = /at$|date|time|stamp|since|until|created|updated|expires|ts$|on$/i
const SORT_ONLY_RE   = /score|rank|seq|order|priority|position/i

function classifyField(name: string, isLast: boolean): 'equality' | 'sort' | 'range' {
  if (RANGE_FIELD_RE.test(name)) return isLast ? 'range' : 'sort'
  if (SORT_ONLY_RE.test(name))   return 'sort'
  return 'equality'
}

function parseIndexShape(rec: { title?: string; mongosh_command?: string; action?: string }): Record<string, number> {
  const titleMatch = (rec.title ?? '').match(/\{([^}]+)\}/)
  const cmdSrc     = rec.mongosh_command ?? rec.action ?? ''
  const cmdMatch   = cmdSrc.match(/createIndex\s*\((\{[^)]+\})/)
  const raw = titleMatch?.[1] ?? cmdMatch?.[1]?.match(/\{([^}]+)\}/)?.[1] ?? ''
  if (!raw) return {}
  const shape: Record<string, number> = {}
  for (const part of raw.split(',')) {
    const [k, v] = part.trim().split(':').map((s) => s.trim().replace(/['"]/g, ''))
    if (k) shape[k] = v === '-1' ? -1 : 1
  }
  return shape
}

function buildESR(shape: Record<string, number>): { equality: string[]; sort: string[]; range: string[] } {
  const keys = Object.keys(shape)
  const esr = { equality: [] as string[], sort: [] as string[], range: [] as string[] }
  keys.forEach((k, i) => {
    esr[classifyField(k, i === keys.length - 1)].push(k)
  })
  return esr
}

function guessFieldSample(fieldName: string): string {
  const lower = fieldName.toLowerCase()
  if (lower === 'deleted' || lower.startsWith('is') || lower.startsWith('has') || lower === 'active' || lower === 'enabled') return 'false'
  if (lower.includes('status')) return '"active"'
  if (lower.includes('type'))   return '"standard"'
  if (lower.endsWith('id') || lower.includes('locationid') || lower.includes('userid') || lower.includes('contactid')) return '"<objectId>"'
  return '"<value>"'
}

function buildQueryPattern(namespace: string, shape: Record<string, number>, esr: ReturnType<typeof buildESR>): string {
  const coll = namespace.split('.').pop() ?? 'collection'
  const filterParts: string[] = []
  for (const f of esr.equality) filterParts.push(`  ${f}: ${guessFieldSample(f)}`)
  for (const f of esr.range)    filterParts.push(`  ${f}: { $gte: <startDate>, $lte: <endDate> }`)
  const filter = filterParts.length ? `{\n${filterParts.join(',\n')}\n}` : '{}'
  const sortFields = [...esr.sort, ...esr.range]
  const sortStr = sortFields.length ? `.sort({ ${sortFields.map((f) => `${f}: ${shape[f] ?? -1}`).join(', ')} })` : ''
  return `db.${coll}.find(${filter})${sortStr}`
}

function formatWeightLabel(w: number): string {
  if (w >= 1e12) return `${(w / 1e12).toFixed(1)}T`
  if (w >= 1e9)  return `${(w / 1e9).toFixed(1)}B`
  if (w >= 1e6)  return `${(w / 1e6).toFixed(1)}M`
  if (w >= 1e3)  return `${(w / 1e3).toFixed(1)}K`
  return String(w)
}

function extractWeight(rec: Partial<Recommendation>): number {
  if (rec.weightNum != null && rec.weightNum > 0) return rec.weightNum
  // weight stored in detail: "Atlas Performance Advisor. Weight: 123456"
  const m = (rec.detail ?? '').match(/Weight:\s*([\d,]+)/)
  if (m) return parseInt(m[1].replace(/,/g, ''), 10)
  return 0
}

export function enrichRecommendation(rec: Recommendation): Recommendation {
  if (rec.queryPattern) return rec  // already enriched (came from server)
  const shape = parseIndexShape(rec)
  if (Object.keys(shape).length === 0) return rec
  const esr         = buildESR(shape)
  const queryPattern = buildQueryPattern(rec.namespace ?? '', shape, esr)
  const w            = extractWeight(rec)
  const weightBreakdown = w > 0 ? {
    weight: w,
    weightLabel: formatWeightLabel(w),
    estimatedExecPerDay: Math.round(w / 5000),
    estimatedDocsScannedPerExec: 5000,
    explanation: `MongoDB scanned ~${formatWeightLabel(w)} unnecessary doc reads (executions × docs_without_index)`,
  } : undefined
  return { ...rec, weightNum: w || undefined, indexShape: shape, esr, queryPattern, weightBreakdown }
}

export function parseDeepReportJSON(text: string): DeepReport | null {
  try {
    const parsed = JSON.parse(text)
    if (!parsed || !parsed.summary || !Array.isArray(parsed.recommendations)) return null
    // Enrich every recommendation with query correlation fields
    const enriched = { ...parsed, recommendations: parsed.recommendations.map(enrichRecommendation) }
    return enriched as DeepReport
  } catch {
    return null
  }
}

// ─── Format helpers ───────────────────────────────────────────────────────────

export function formatBytes(mb: number | string): string {
  const n = parseFloat(String(mb))
  if (isNaN(n)) return '—'
  if (n >= 1048576) return `${(n / 1048576).toFixed(1)} TB`
  if (n >= 1024) return `${(n / 1024).toFixed(1)} GB`
  if (n >= 1) return `${n.toFixed(1)} MB`
  if (n > 0) return `${(n * 1024).toFixed(0)} KB`
  return '0 B'
}

export function formatBytesPerSec(bytesPerSec: number): string {
  if (bytesPerSec >= 1073741824) return `${(bytesPerSec / 1073741824).toFixed(1)} GB/s`
  if (bytesPerSec >= 1048576) return `${(bytesPerSec / 1048576).toFixed(1)} MB/s`
  if (bytesPerSec >= 1024) return `${(bytesPerSec / 1024).toFixed(1)} KB/s`
  return `${bytesPerSec.toFixed(0)} B/s`
}

export function formatNum(n: number | string): string {
  const v = typeof n === 'string' ? parseFloat(n) : n
  if (isNaN(v)) return '—'
  if (v >= 1e18) return v.toExponential(1)
  if (v >= 1e15) return `${(v / 1e15).toFixed(1)}Q`
  if (v >= 1_000_000_000_000) return `${(v / 1_000_000_000_000).toFixed(1)}T`
  if (v >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(1)}B`
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`
  return v.toLocaleString()
}

export function formatMs(ms: number | string): string {
  const v = typeof ms === 'string' ? parseFloat(ms) : ms
  if (isNaN(v)) return '—'
  if (v >= 1e15) return v.toExponential(1)
  if (v >= 86_400_000) return `${(v / 86_400_000).toFixed(1)}d`
  if (v >= 3_600_000) return `${(v / 3_600_000).toFixed(1)}h`
  if (v >= 60_000) return `${(v / 60_000).toFixed(1)}m`
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}s`
  return `${Math.round(v)}ms`
}

export function formatCurrency(n: number): string {
  if (isNaN(n)) return '—'
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`
  return `$${n.toFixed(0)}`
}

export function formatDate(dateStr: string): string {
  if (!dateStr) return '—'
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  } catch {
    return dateStr
  }
}

export function indexTrackingDays(since: string): number {
  if (!since) return 0
  try {
    return Math.floor((Date.now() - new Date(since).getTime()) / 86_400_000)
  } catch {
    return 0
  }
}
